/**
 * Unified reforecast-entry model — the single list of scrub targets that the axis
 * ticks, the drag playhead, and the report-modal carousel all iterate, so MANUAL
 * (↻) and AUTOMATIC (panel/async) reforecasts scrub through one identical
 * mechanism.
 *
 * Each entry is a moment the user can rewind to:
 *   - `generation` / `manual` / `live` — MANUAL-side moments, resolvable INLINE
 *     (the full snapshot is already in `active.history` / on the run), so scrub
 *     is instant.
 *   - `async` — one per scheduled `forecast` report-pointer, carrying only
 *     `{ rowKey, fireTs }`; the full report is fetched LAZILY on scrub (via the
 *     report read-bridge, TTL-cached) — never here, so building the list costs no
 *     network and every fire gets a tick.
 *
 * When two moments collide, the MANUAL entry wins over the async one. The `live`
 * marker is the last entry and carries the newest moment, absorbing any vintage
 * from the same second.
 */

import { forecastPointers, resolutionPointers } from "./reportPointer.js";
import { STATUS } from "./model.js";
import { firedAfterResolution } from "./postResolutionRuns.js";
import { isVoidedContract } from "./contractStatus.js";
import { parseResolveDateLocal } from "../formatDates.js";

// Kind precedence when two moments collide on the exact same millisecond: the
// live marker owns its instant (clicking it returns to latest), then a manual
// snapshot (full inline fidelity) beats an async fire (lazy, slimmer report). A
// resolution never contends with a forecast for an instant, but is ranked top
// defensively.
const _KIND_RANK = { resolution: 4, live: 3, generation: 2, manual: 2, async: 1 };

/**
 * The live sentinel's moment: the newest of the forecast's creation, updates and fires.
 * `lastReforecastedAt` is NOT hydrated from the wire, so the live moment is the MAX of every moment
 * we know, which keeps it the latest forecast in BOTH position AND time. Each candidate keeps its
 * ORIGINAL iso string (no toISOString): the sentinel's `moment` must stay byte-identical to the
 * wire string so it round-trips through fcMoments → scrubToMoment → findEntryByMoment unchanged.
 *
 * Without `lastReforecastedAt` (a run in progress has already moved it to its own start), this is
 * the moment of the latest version that finished.
 *
 * @param {Array} history `active.history`; may be undefined.
 * @param {Array} reportPointers `active.reportPointers`.
 * @param {{ createdAt?: string, lastReforecastedAt?: string }} [opts]
 * @returns {{ms: number, iso: string}|null} null when no moment parses
 */
export function newestForecastMoment(
  history,
  reportPointers,
  { createdAt, lastReforecastedAt } = {},
) {
  let best = null;
  const consider = (iso) => {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return;
    if (!best || ms > best.ms) best = { ms, iso };
  };
  consider(lastReforecastedAt);
  consider(createdAt);
  for (const h of Array.isArray(history) ? history : []) {
    consider(h && h.snapshotAt);
    // Legacy rows may lack `snapshotAt`; fall back to `generatedAt` (as
    // buildReforecastMoments does) so the moment can't collapse to createdAt and
    // sink behind the manual vintages. For a current row snapshotAt is later, so
    // this changes nothing; each generatedAt (except the first) also equals a
    // prior entry's snapshotAt, so it never introduces a spurious max.
    consider(h && h.generatedAt);
  }
  for (const p of forecastPointers(reportPointers)) consider(p.fire_ts);
  return best;
}

/**
 * Build the unified, time-ordered reforecast-entry list.
 *
 * @param {Array} history       `active.history` (manual ↻ snapshots); may be undefined.
 * @param {Array} reportPointers `active.reportPointers` (async report-pointer history).
 * @param {{ createdAt?: string, lastReforecastedAt?: string, resolution?: object|null }} opts
 *   `createdAt` = the generation moment; `lastReforecastedAt` = the latest
 *   reforecast (the "live" moment). `resolution` = `active.resolution` (the
 *   hydrated newest verdict, or null) — used to enrich the matching resolution
 *   entry with its full report inline, or to synthesize an entry when the
 *   resolution pointer hasn't landed on the wire yet.
 * @returns {Array<{atMs:number, moment:string,
 *   kind:'generation'|'manual'|'async'|'live'|'resolution',
 *   source:string, isLive:boolean, isResolution?:boolean,
 *   status?:string, snapshot?:object, rowKey?:string, fireTs?:string}>}
 *   Ordering: forecast vintages (old→new) → live sentinel → resolution
 *   events (old→new). A resolved question's tail is its NEWEST resolution.
 */
export function buildReforecastEntries(
  history,
  reportPointers,
  { createdAt, lastReforecastedAt, resolution } = {},
) {
  const vintages = [];

  // Entries are keyed by generatedAt, which must match the key App.jsx's viewSnap
  // lookup uses.
  for (const h of Array.isArray(history) ? history : []) {
    const ms = Date.parse(h && h.generatedAt);
    if (!Number.isNaN(ms)) {
      vintages.push({
        atMs: ms,
        moment: h.generatedAt,
        kind: "manual",
        source: "manual",
        isLive: false,
        snapshot: h,
      });
    }
  }

  for (const p of forecastPointers(reportPointers)) {
    const ms = Date.parse(p.fire_ts);
    if (!Number.isNaN(ms)) {
      vintages.push({
        atMs: ms,
        moment: p.fire_ts,
        kind: "async",
        source: "panel",
        isLive: false,
        rowKey: p.row_key,
        fireTs: p.fire_ts,
        // The engine that ran this fire — a scheduled reforecast carries its own, which
        // need not be the forecast's current one. Names the report's forecast method.
        forecaster: typeof p.forecaster === "string" ? p.forecaster : null,
      });
    }
  }

  vintages.sort((a, b) => a.atMs - b.atMs);
  const deduped = [];
  for (const e of vintages) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.atMs === e.atMs) {
      if ((_KIND_RANK[e.kind] || 0) > (_KIND_RANK[prev.kind] || 0)) deduped[deduped.length - 1] = e;
      continue;
    }
    deduped.push(e);
  }

  // ── The live marker — the LAST forecast entry (resolution entries follow it).
  const live = newestForecastMoment(history, reportPointers, { createdAt, lastReforecastedAt });
  const liveMs = live ? live.ms : NaN;
  const liveIso = live ? live.iso : null;
  // The vintage sharing the live instant is absorbed by the sentinel, compared at
  // whole-SECOND granularity rather than exact milliseconds. An ON-DEMAND fire's
  // compact `fire_ts` (`YYYYMMDD_hhmmss`, normalized to …:ss.000Z) carries only
  // whole seconds, while the `createdAt` it produced carries milliseconds, so an
  // exact-ms match would miss them. Second granularity is safe because two
  // genuinely distinct fires for one contract never land in the same second (a
  // forecast run takes seconds to minutes), and an earlier fire in a DIFFERENT
  // second still survives as its own vintage.
  const _sameSecond = (a, b) => Math.floor(a / 1000) === Math.floor(b / 1000);
  const out = Number.isNaN(liveMs) ? deduped : deduped.filter((e) => !_sameSecond(e.atMs, liveMs));
  if (!Number.isNaN(liveMs)) {
    out.push({
      atMs: liveMs,
      // Label by whether any OTHER entry survives (`out` already dropped the
      // vintage sharing the live second). If none does, this sentinel is the sole
      // stop and IS the generation moment ("generation"); otherwise it is the
      // newest, distinct "live" state. Keying on `out.length` instead of
      // `history.length` covers async-only forecasts.
      kind: out.length ? "live" : "generation",
      moment: liveIso,
      source: "manual",
      isLive: true,
    });
  }

  // ── Resolution events — DISTINCT terminal entries appended AFTER the forecast
  // list, in `attempted_at` order. Each is its own scrub stop with its own verdict
  // `status` (carried inline on the pointer — no fetch needed). The newest is the
  // tail, so it is the default / "return to latest" position on a resolved
  // question. A forecast entry NEVER carries a verdict: it lives only on these
  // entries.
  const resEntries = [];
  for (const p of resolutionPointers(reportPointers)) {
    // parseResolveDateLocal (not Date.parse): a date-only `attempted_at` is LOCAL
    // midnight, consistent with the rest of the UI, so entries can't shift a day /
    // mis-order across a day boundary in the user's timezone.
    const rd = parseResolveDateLocal(p.attempted_at);
    const ms = rd ? rd.getTime() : NaN;
    if (Number.isNaN(ms)) continue;
    resEntries.push({
      atMs: ms,
      moment: p.attempted_at,
      kind: "resolution",
      source: p.source || "resolution",
      isLive: false,
      isResolution: true,
      status: p.status ?? null,
    });
  }
  // Reconcile the hydrated `active.resolution` (the newest verdict, with its full
  // report) with the pointers: enrich a same-instant pointer entry with the inline
  // snapshot (so scrubbing to it is instant, no read), or synthesize an entry when
  // the pointer hasn't landed yet (resolution hydrates before its pointer fans out).
  // Skip an `in_flight` resolution — same "settled attempts only" contract that
  // resolutionPointers() enforces on the pointer path; an in-flight verdict must
  // not become a selectable scrub stop even when it already carries a date.
  if (
    resolution &&
    resolution.status !== "in_flight" &&
    (resolution.resolved_at || resolution.resolution_date)
  ) {
    const resMoment = resolution.resolved_at || resolution.resolution_date;
    const _rd = parseResolveDateLocal(resMoment);
    const ms = _rd ? _rd.getTime() : NaN;
    if (!Number.isNaN(ms)) {
      const match = resEntries.find((e) => e.atMs === ms);
      if (match) {
        match.snapshot = resolution;
        if (resolution.status != null) match.status = resolution.status;
      } else {
        resEntries.push({
          atMs: ms,
          moment: resMoment,
          kind: "resolution",
          source: "resolution",
          isLive: false,
          isResolution: true,
          status: resolution.status ?? null,
          snapshot: resolution,
        });
      }
    }
  }
  resEntries.sort((a, b) => a.atMs - b.atMs);
  for (const e of resEntries) {
    const prev = out[out.length - 1];
    if (prev && prev.isResolution && prev.atMs === e.atMs) {
      if (!prev.snapshot && e.snapshot) out[out.length - 1] = e;
      continue;
    }
    out.push(e);
  }
  return out;
}

/**
 * @param {Array} entries
 * @param {string} moment
 */
export function findEntryByMoment(entries, moment) {
  if (!Array.isArray(entries) || !moment) return null;
  return entries.find((e) => e.moment === moment) || null;
}

/**
 * The moment of the latest FORECAST — the live sentinel, which is not the tail once
 * resolution entries follow it. Falls back to the tail when no entry is live, and null
 * for an empty list.
 *
 * @param {Array} entries buildReforecastEntries output
 * @returns {string|null}
 */
export function liveSentinelMoment(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return list.find((e) => e.isLive)?.moment ?? list[list.length - 1]?.moment ?? null;
}

/**
 * Is `moment` the "return to latest" position — the TAIL entry (the newest
 * resolution on a resolved question, else the live forecast sentinel)? Selecting
 * the tail returns the scrub to live (viewMomentIso = null), so every EARLIER
 * entry stays a real scrub target. Keying off the tail rather than the `isLive`
 * flag keeps the live forecast sentinel selectable once resolutions follow it. An
 * unknown/empty moment defaults to true.
 *
 * @param {Array} entries
 * @param {string} moment
 * @returns {boolean}
 */
export function isReturnToLatestMoment(entries, moment) {
  const list = Array.isArray(entries) ? entries : [];
  if (!list.length) return true;
  const e = findEntryByMoment(list, moment);
  if (!e) return true;
  return e.moment === list[list.length - 1].moment;
}

/**
 * Is the CURRENTLY-VIEWED scrub position the live forecast sentinel (the latest
 * FORECAST)? Keyed off the selected ENTRY's `isLive` flag, NOT "is it the tail",
 * because resolution events follow the sentinel. At rest (`viewMomentIso == null`)
 * the viewed position is the tail entry, so this returns true only when that tail
 * IS the sentinel. An unknown/empty position → false (don't claim LIVE).
 *
 * @param {Array} entries
 * @param {string|null} viewMomentIso
 * @returns {boolean}
 */
export function isLiveScrubPosition(entries, viewMomentIso) {
  const list = Array.isArray(entries) ? entries : [];
  if (!list.length) return false;
  const e = viewMomentIso ? findEntryByMoment(list, viewMomentIso) : list[list.length - 1];
  return !!e?.isLive;
}

/**
 * `run` as the CURRENTLY-VIEWED position may show it. A past forecast version predates the
 * outcome, so there the resolution is dropped and a void reads as open; at rest or on a
 * resolution entry `run` is returned unchanged.
 *
 * Dropping only the status is not enough: a multi-value verdict names its winner by slot and
 * carries no status, and `applyResolutionToDistribution` collapses onto that slot by itself.
 *
 * @param {object|null} run the displayed run (`displayActive`)
 * @param {{viewMomentIso: (string|null), viewEntry: (object|null)}} position the scrub position
 * @returns {object|null}
 */
export function withoutVerdictAtPastVersion(run, { viewMomentIso, viewEntry }) {
  if (!run || !viewMomentIso || viewEntry?.isResolution) return run;
  const voided = isVoidedContract(run);
  if (run.resolution == null && !voided) return run;
  return { ...run, resolution: null, ...(voided ? { contractStatus: null } : {}) };
}

/**
 * Materialize an ASYNC entry into the same snapshot shape a MANUAL `active.history`
 * entry has, from a fetched report source (`reportPayloadToSource` output), so the
 * rewind path is identical for both. Returns null when the report couldn't be
 * fetched/mapped (the caller then keeps the live view). The displayed probability
 * is the FIRE's prediction (what the forecast said at that moment), falling back
 * to the contract probability.
 *
 * @param {{fireTs:string}} entry an async entry from buildReforecastEntries
 * @param {object|null} source result of reportPayloadToSource (or null on miss)
 * @returns {object|null}
 */
export function asyncEntrySnapshot(entry, source) {
  if (!entry || !source) return null;
  // Only a FINITE fire prediction is used: a NaN/Infinity would flow into
  // contract.probability and render as 0%/garbage.
  const cp = source.contract && source.contract.probability;
  const prob = Number.isFinite(source.serverPrediction)
    ? source.serverPrediction
    : Number.isFinite(cp)
      ? cp
      : null;
  // The vintage's distribution: the contract's own when it carries one (an on-demand
  // run), else the report's top-level one (a scheduled fire, whose contract is empty).
  const forecastValue = source.contract?.forecast_value ?? source.forecastValue ?? null;
  return {
    generatedAt: entry.fireTs,
    snapshotAt: entry.fireTs,
    engine: entry.forecaster ?? null,
    contract: {
      ...(source.contract || {}),
      probability: prob,
      ...(forecastValue != null ? { forecast_value: forecastValue } : {}),
    },
    // NULL, never `[]`, when the payload carries no trigger block or its trigger-gen
    // FAILED. App.jsx picks a vintage's events as `snap.events ? ... : a.events`, and
    // `[]` is TRUTHY, so it would defeat the fallback to the accumulated live
    // timeline. The server reports a failed trigger-gen as an EMPTY array plus
    // `postprocess_status: "failed"`, so testing shape alone is not enough.
    events:
      Array.isArray(source.events) && source.postprocessStatus !== "failed" ? source.events : null,
    // Carried so a consumer can tell a failed trigger-gen fire from a genuinely empty
    // one without re-reading the payload.
    postprocessStatus: source.postprocessStatus ?? null,
    serverExplanation: source.serverExplanation ?? null,
    report: source.report ?? null,
    serverContext: source.serverContext ?? null,
    // The shift verdict for THIS fire: `shift`/`shiftDisplay` are passed through from
    // `reportPayloadToSource` and set to an explicit `null`, never omitted, so a
    // consumer can tell "this fire carries no verdict" from "this snapshot shape does
    // not carry the field".
    shift: source.shift ?? null,
    shiftDisplay: source.shiftDisplay ?? null,
    alternateForecast: source.alternateForecast ?? null,
    _async: true,
  };
}

/**
 * Keep-previous-while-loading transition for the scrubbed snapshot. During an async
 * fetch window `deriveScrubView` yields a null `resolvedSnap` (scrubbing=true), and
 * callers would fall back to the LIVE forecast, flashing "latest" while scrubbing
 * A→B. This holds the last resolved snapshot through the load instead. The first
 * scrub straight from live, with nothing held yet, shows live until the fetch
 * settles, and a miss falls back to live.
 *
 * @param {object|null} held the previously-held snapshot (ref.current)
 * @param {{selected:boolean, scrubbing:boolean, resolvedSnap:object|null}} s
 * @returns {{display:(object|null), next:(object|null)}}
 */
export function holdSnapshotThroughLoad(held, { selected, scrubbing, resolvedSnap }) {
  if (!selected) return { display: null, next: null };
  if (scrubbing) {
    // Never display a snapshot without carrying it forward, so display and next stay
    // consistent on the `held == null` fallback.
    const display = held ?? resolvedSnap;
    return { display, next: display };
  }
  return { display: resolvedSnap, next: resolvedSnap };
}

/**
 * Pure core of `useReforecastScrub`, testable without a React runtime. Given the
 * selected `entry` and the keyed async cache `{key, snap}`, an async entry is
 * resolved when the cache key equals its `rowKey` (`snap` may be null on a settled
 * miss, and the caller keeps the live view); a key mismatch means a fetch is needed.
 *
 * @param {object|null} entry a buildReforecastEntries entry, or null (live)
 * @param {{key:(string|null), snap:(object|null)}} asyncState the rowKey-keyed cache
 * @returns {{viewSnap:(object|null), scrubbing:boolean, needsFetch:boolean}}
 */
export function deriveScrubView(entry, asyncState) {
  if (!entry) return { viewSnap: null, scrubbing: false, needsFetch: false };
  if (entry.kind === "manual" || entry.kind === "generation") {
    return { viewSnap: entry.snapshot || null, scrubbing: false, needsFetch: false };
  }
  if (entry.kind === "resolution") {
    // Older resolution attempts have no fetchable per-attempt body, so they are
    // status-only (null snap, no fetch); the report modal's own resolution overlay
    // supplies the body for the newest.
    return { viewSnap: entry.snapshot || null, scrubbing: false, needsFetch: false };
  }
  if (entry.kind === "async") {
    const settled = asyncState && asyncState.key === entry.rowKey;
    return {
      viewSnap: settled ? asyncState.snap : null,
      scrubbing: !settled,
      needsFetch: !settled,
    };
  }
  return { viewSnap: null, scrubbing: false, needsFetch: false };
}

/**
 * Pick the newest reforecast for a contract. Returns the async fire iff a forecast
 * pointer's fire_ts is STRICTLY newer than the live moment (ties → live, so a
 * manual state is never displaced by a same-instant fire). A null/unparseable live
 * moment lets any fire win.
 *
 * @param {string|null} liveMomentIso the live/manual state's moment
 * @param {Array} reportPointers the run's report_pointers
 * @returns {{kind:'live'} | {kind:'async', rowKey:string, fireTs:string}}
 */
export function selectNewestReforecast(liveMomentIso, reportPointers) {
  const liveMs = liveMomentIso ? Date.parse(liveMomentIso) : NaN;
  let best = null;
  for (const p of forecastPointers(reportPointers)) {
    const ms = Date.parse(p.fire_ts);
    if (Number.isNaN(ms)) continue;
    if (best === null || ms > best.ms) best = { ms, rowKey: p.row_key, fireTs: p.fire_ts };
  }
  if (best === null) return { kind: "live" };
  if (!Number.isNaN(liveMs) && best.ms <= liveMs) return { kind: "live" };
  return { kind: "async", rowKey: best.rowKey, fireTs: best.fireTs };
}

/**
 * Build the runReforecasted payload that promotes an async fire's fetched report
 * (reportPayloadToSource output) into a run's CURRENT forecast. The displayed
 * probability is the FIRE's prediction, falling back to the contract's. `history`
 * is intentionally NOT included so the run's manual history is left untouched.
 *
 * @param {object} source result of reportPayloadToSource (a fetched forecast report)
 * @param {string} fireTs the async fire's moment (becomes lastReforecastedAt)
 * @returns {{contract, serverPrediction, events, serverExplanation, report, serverContext,
 *   lastReforecastedAt}}
 */
export function newestReforecastPatch(source, fireTs) {
  const cp = source.contract && source.contract.probability;
  const prob = Number.isFinite(source.serverPrediction)
    ? source.serverPrediction
    : Number.isFinite(cp)
      ? cp
      : null;
  return {
    contract: { ...(source.contract || {}), probability: prob },
    serverPrediction: prob,
    events: Array.isArray(source.events) ? source.events : [],
    serverExplanation: source.serverExplanation ?? null,
    report: source.report ?? null,
    serverContext: source.serverContext ?? null,
    lastReforecastedAt: fireTs,
  };
}

/**
 * Guard for the newest-is-live overlay: may we promote an async fire (fireTs) into
 * the current run, or would it clobber fresher state? The overlay's fetch is async,
 * so by the time it is ready to dispatch `runReforecasted` the user may have
 * reforecasted manually, cancelled, or moved the run on, and `runReforecasted`
 * would overwrite `status`/`contract`/`events`/`serverContext`/`lastReforecastedAt`
 * unconditionally. This is checked against the LIVE run (read at dispatch time),
 * not the snapshot the overlay started from. A null/unparseable current
 * lastReforecastedAt is the hydrated default, so it is safe to apply.
 *
 * A fire after the question resolved is never promoted (see postResolutionRuns). One
 * promoted before the tab learned of the resolution is undone by the reducer, which
 * restores the snapshot that promotion recorded.
 *
 * @param {object|null|undefined} run the LIVE run (runsById[runId]) at dispatch time
 * @param {string} fireTs the candidate fire's fire_ts
 * @returns {boolean}
 */
export function shouldApplyNewestReforecast(run, fireTs) {
  if (!run) return false;
  if (run.status !== STATUS.COMPLETED) return false;
  const fireMs = Date.parse(fireTs);
  if (Number.isNaN(fireMs)) return false;
  if (firedAfterResolution(run, fireTs)) return false;
  const currentMs = run.lastReforecastedAt ? Date.parse(run.lastReforecastedAt) : NaN;
  if (!Number.isNaN(currentMs) && currentMs >= fireMs) return false;
  return true;
}

/**
 * Position of a scrubbed moment within the ordered unified moment list, shared by
 * the timeline scrub pill and the report-modal carousel so their counters and
 * steppers can never drift. The list's LAST entry is the live sentinel, so the live
 * state is index total-1, and stepping `nextMoment` off the newest past vintage
 * yields the live sentinel moment.
 *
 * @param {string|null} momentIso the selected moment (null = live)
 * @param {Array<string>} moments the ordered moment strings (fcMoments)
 * @returns {{pos:number, total:number, isLive:boolean,
 *   prevMoment:(string|null), nextMoment:(string|null)}}
 */
export function scrubPosition(momentIso, moments) {
  const list = Array.isArray(moments) ? moments : [];
  const total = list.length;
  const isLive = momentIso == null;
  let pos = isLive ? total - 1 : list.indexOf(momentIso);
  if (pos < 0 && !isLive && total > 0) pos = total - 1;
  const prevMoment = pos > 0 ? list[pos - 1] : null;
  const nextMoment = pos >= 0 && pos < total - 1 ? list[pos + 1] : null;
  return { pos, total, isLive, prevMoment, nextMoment };
}
